import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'

export const INELIGIBLE_PAGE_SIZE = 25

const EXCLUDED_BATCH_PREFIXES = ['REFIXED_', 'FUNDINGEXT_']

async function fetchAllPages<T>(
    url: string,
    makeData: (page: number) => object,
    extractItems: (data: any) => T[],
    onFirstPage?: (data: any) => void
): Promise<T[]> {
    const first = await api({ url, data: makeData(1) }).catch(() => ({ data: { success: false } }))
    if (!first.data?.success) return []

    onFirstPage?.(first.data)

    const items: T[] = extractItems(first.data)
    const pag = first.data.pagination ?? {}
    const totalPages: number = pag.total_pages ?? pag.totalPages ?? (pag.total && pag.per_page ? Math.ceil(pag.total / pag.per_page) : 1)

    if (totalPages > 1) {
        const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
                api({ url, data: makeData(i + 2) }).catch(() => ({ data: { success: false } }))
            )
        )
        rest.forEach((r) => {
            if (r.data?.success) items.push(...extractItems(r.data))
        })
    }

    return items
}

interface FundingBatch {
    batch_id: string
    introducer: string
    allocation_date?: string
    created_at?: string
}

interface RemovedClaim {
    funder_case_ref: string
    introducer_case_ref: string
    law_firm_secondary_reference: string
    agreement_number?: string | null
    agreement_start_date?: string | null
    loan_value: number
    quantum_individual: number
    defendant: string
    cfa_date: string
    client_status: string
    audit_removed_date: string
}

interface RemovedClient {
    funder_client_id: number
    title: string
    first_name: string
    last_name: string
    email_address: string
    dob: string
    claims: RemovedClaim[]
    claimsCount: number
    totalValue: number
}

interface ReasonCount {
    reason: string
    count: number
}

interface AggregatedRemovedClient extends RemovedClient {
    batch_id: string
    introducer: string
}

interface NormClaim {
    funder_client_id?: number
    funder_case_ref?: string
    introducer_case_ref?: string
    law_firm_secondary_reference?: string
    agreement_number?: string | null
    agreement_start_date?: string | null
    batch_id?: string
    introducer?: string
    defendant: string
    client_status: string
    cfa_date: string
    loan_value: number
    quantum_individual: number
    audit_removed_date?: string
}

type ExplorerFilterKey = 'search' | 'loan' | 'quantum' | 'cfa' | 'defendant' | 'introducer' | 'batch' | 'status'

interface ActiveClaim {
    funder_case_ref: string
    introducer_case_ref: string
    law_firm_case_reference: string
    law_firm_secondary_reference: string
    defendant: string
    client_status: string
    cfa_date: string
    loan_value: number
    quantum_individual: number
    quantum_client_cumulative: number
    allocated: string
    submitted: string
    api_transferred: string
}

interface ActiveClient {
    funder_client_id: number
    claims: ActiveClaim[]
    total_value: number
    claims_count: number
}

interface AggregatedActiveClient extends ActiveClient {
    batch_id: string
}

interface BatchAllocateSummary {
    total_clients: number
    total_book_value: number
}

interface CohortStats {
    clientCount: number
    claimCount: number
    totalValue: number
    missingCfaPct: number
    statusBreakdown: { status: string; count: number; pct: number }[]
    topDefendants: { defendant: string; count: number; pct: number }[]
}

const LOAN_BUCKETS: { label: string; test: (v: number) => boolean }[] = [
    { label: '£0-£100', test: (v) => v < 100 },
    { label: '£100-£250', test: (v) => v >= 100 && v < 250 },
    { label: '£250-£500', test: (v) => v >= 250 && v < 500 },
    { label: '£500-£750', test: (v) => v >= 500 && v < 750 },
    { label: '£750-£1k', test: (v) => v >= 750 && v < 1000 },
    { label: '£1k-£2k', test: (v) => v >= 1000 && v < 2000 },
    { label: '£2k-£5k', test: (v) => v >= 2000 && v < 5000 },
    { label: '£5k+', test: (v) => v >= 5000 },
]

const formatCurrency = (value: number): string =>
    `£${(value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function buildDefendantChart(stayed: NormClaim[], removed: NormClaim[]) {
    const totals: Record<string, number> = {}
    ;[...stayed, ...removed].forEach((c) => {
        const d = c.defendant || 'Unknown'
        totals[d] = (totals[d] || 0) + 1
    })
    const labels = Object.entries(totals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([d]) => d)

    const stayedCounts = labels.map((d) => stayed.filter((c) => (c.defendant || 'Unknown') === d).length)
    const kickedCounts = labels.map((d) => removed.filter((c) => (c.defendant || 'Unknown') === d).length)
    return { labels, stayedCounts, kickedCounts }
}

function buildStatusChart(stayed: NormClaim[], removed: NormClaim[]) {
    const totals: Record<string, number> = {}
    ;[...stayed, ...removed].forEach((c) => {
        const s = c.client_status || 'Unknown'
        totals[s] = (totals[s] || 0) + 1
    })
    const labels = Object.entries(totals)
        .sort((a, b) => b[1] - a[1])
        .map(([s]) => s)

    const stayedCounts = labels.map((s) => stayed.filter((c) => (c.client_status || 'Unknown') === s).length)
    const kickedCounts = labels.map((s) => removed.filter((c) => (c.client_status || 'Unknown') === s).length)
    return { labels, stayedCounts, kickedCounts }
}

function buildLoanChart(stayed: NormClaim[], removed: NormClaim[]) {
    const labels = LOAN_BUCKETS.map((b) => b.label)
    const stayedCounts = LOAN_BUCKETS.map((b) => stayed.filter((c) => b.test(c.loan_value || 0)).length)
    const kickedCounts = LOAN_BUCKETS.map((b) => removed.filter((c) => b.test(c.loan_value || 0)).length)
    return { labels, stayedCounts, kickedCounts }
}

function buildCfaChart(stayed: NormClaim[], removed: NormClaim[]) {
    const yearOf = (c: NormClaim) => {
        if (!c.cfa_date) return 'Missing'
        const y = new Date(c.cfa_date).getFullYear()
        return isNaN(y) ? 'Missing' : String(y)
    }
    const allYears = new Set([...stayed, ...removed].map(yearOf))
    const sorted = Array.from(allYears)
        .filter((y) => y !== 'Missing')
        .sort()
    if (allYears.has('Missing')) sorted.push('Missing')

    const stayedCounts = sorted.map((y) => stayed.filter((c) => yearOf(c) === y).length)
    const kickedCounts = sorted.map((y) => removed.filter((c) => yearOf(c) === y).length)
    return { labels: sorted, stayedCounts, kickedCounts }
}

function buildMissingFieldsTable(stayed: NormClaim[], removed: NormClaim[]) {
    const isMissing = {
        'CFA date': (c: NormClaim) => !c.cfa_date,
        'Defendant': (c: NormClaim) => !c.defendant,
        'Client status': (c: NormClaim) => !c.client_status,
        'Loan value': (c: NormClaim) => !c.loan_value,
        'Quantum': (c: NormClaim) => !c.quantum_individual,
    } as Record<string, (c: NormClaim) => boolean>

    return Object.entries(isMissing)
        .map(([field, check]) => {
            const stayedPct = stayed.length ? Math.round((stayed.filter(check).length / stayed.length) * 100) : 0
            const kickedPct = removed.length ? Math.round((removed.filter(check).length / removed.length) * 100) : 0
            return { field, stayedPct, kickedPct, diff: kickedPct - stayedPct }
        })
        .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
}

function buildPassRateTable(stayed: NormClaim[], removed: NormClaim[]) {
    return LOAN_BUCKETS.map((b) => {
        const s = stayed.filter((c) => b.test(c.loan_value || 0)).length
        const k = removed.filter((c) => b.test(c.loan_value || 0)).length
        const total = s + k
        return { band: b.label, total, stayed: s, passRate: total ? Math.round((s / total) * 100) : 0 }
    }).filter((r) => r.total > 0)
}

const deriveCohortStats = (claims: NormClaim[], clientCount: number, totalValue: number): CohortStats => {
    const claimCount = claims.length
    const missingCfa = claims.filter((c) => !c.cfa_date).length
    const missingCfaPct = claimCount ? Math.round((missingCfa / claimCount) * 100) : 0

    const statusCounts: Record<string, number> = {}
    claims.forEach((c) => {
        const s = c.client_status || 'Unknown'
        statusCounts[s] = (statusCounts[s] || 0) + 1
    })
    const statusBreakdown = Object.entries(statusCounts)
        .map(([status, count]) => ({ status, count, pct: claimCount ? Math.round((count / claimCount) * 100) : 0 }))
        .sort((a, b) => b.count - a.count)

    const defCounts: Record<string, number> = {}
    claims.forEach((c) => {
        const d = c.defendant || 'Unknown'
        defCounts[d] = (defCounts[d] || 0) + 1
    })
    const topDefendants = Object.entries(defCounts)
        .map(([defendant, count]) => ({ defendant, count, pct: claimCount ? Math.round((count / claimCount) * 100) : 0 }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)

    return { clientCount, claimCount, totalValue, missingCfaPct, statusBreakdown, topDefendants }
}

export const useIneligibleFundingClientsViewState = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [batches, setBatches] = useState<FundingBatch[]>([])
    const [removedClients, setRemovedClients] = useState<AggregatedRemovedClient[]>([])
    const [activeClients, setActiveClients] = useState<AggregatedActiveClient[]>([])
    const [reasonsByBatch, setReasonsByBatch] = useState<Record<string, ReasonCount[]>>({})
    const [batchAllocateSummaries, setBatchAllocateSummaries] = useState<Record<string, BatchAllocateSummary>>({})

    const [batchFilter, setBatchFilter] = useState<string>('all')
    const [introducerFilter, setIntroducerFilter] = useState<string>('all')
    const [search, setSearch] = useState<string>('')
    const [currentPage, setCurrentPage] = useState(1)
    const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set())
    const [detailsModalClientId, setDetailsModalClientId] = useState<number | null>(null)

    const [explorerLoanValuesSelected, setExplorerLoanValuesSelected] = useState<Set<number>>(new Set())
    const [explorerQuantumMin, setExplorerQuantumMin] = useState(0)
    const [explorerQuantumMax, setExplorerQuantumMax] = useState(Infinity)
    const [pendingQuantumMin, setPendingQuantumMin] = useState(0)
    const [pendingQuantumMax, setPendingQuantumMax] = useState(Infinity)
    const [quantumApplying, setQuantumApplying] = useState(false)
    const [explorerDefendants, setExplorerDefendants] = useState<string[]>([])
    const [explorerIntroducers, setExplorerIntroducers] = useState<string[]>([])
    const [explorerBatches, setExplorerBatches] = useState<string[]>([])
    const [explorerStatuses, setExplorerStatuses] = useState<string[]>([])
    const [explorerCfa, setExplorerCfa] = useState<'all' | 'present' | 'missing'>('all')
    const [defendantSort, setDefendantSort] = useState<'name' | 'claims'>('claims')
    const [showExplorerClaimsModal, setShowExplorerClaimsModal] = useState(false)
    const [openExplorerFilter, setOpenExplorerFilter] = useState<ExplorerFilterKey | null>(null)
    const [filterPopoverPosition, setFilterPopoverPosition] = useState<{ top: number; left: number; width: number } | null>(null)
    const explorerFilterBarRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (detailsModalClientId == null) return
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setDetailsModalClientId(null)
        }
        window.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = prevOverflow
            window.removeEventListener('keydown', onKey)
        }
    }, [detailsModalClientId])

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            setError(null)
            try {
                const batchesResponse = await api({ url: '/funding-batches', data: {} })
                if (!batchesResponse.data?.success) {
                    setError(batchesResponse.data?.message || 'Failed to load batches')
                    setLoading(false)
                    return
                }

                                const allBatches: FundingBatch[] = (batchesResponse.data.batches || [])
                    .filter((b: FundingBatch) =>
                        b.introducer !== 'FunderAdditional' &&
                        !EXCLUDED_BATCH_PREFIXES.some(prefix => b.batch_id.startsWith(prefix))
                    )
                setBatches(allBatches)

                if (allBatches.length === 0) {
                    setLoading(false)
                    return
                }

                const summariesMap: Record<string, BatchAllocateSummary> = {}

                const [removedResults, reasonsResponses, activeResults] = await Promise.all([
                    Promise.all(
                        allBatches.map((batch) =>
                            fetchAllPages<RemovedClient>(
                                '/funding-batch-audit-removed',
                                (page) => ({ batchId: batch.batch_id, page, limit: 500 }),
                                (data) => data.removedClients ?? []
                            )
                        )
                    ),
                    Promise.all(
                        allBatches.map((batch) =>
                            api({
                                url: '/funding-batch-audit-removal-reasons',
                                data: { batchId: batch.batch_id },
                            }).catch((err) => ({ data: { success: false, error: err } }))
                        )
                    ),
                    Promise.all(
                        allBatches.map((batch) =>
                            fetchAllPages<ActiveClient>(
                                '/funding-batch-details',
                                (page) => ({ batchId: batch.batch_id, page, limit: 500 }),
                                (data) => data.clients ?? [],
                                (data) => {
                                    if (data.batch_summary) {
                                        summariesMap[batch.batch_id] = {
                                            total_clients: data.batch_summary.total_clients ?? 0,
                                            total_book_value: data.batch_summary.total_book_value ?? 0,
                                        }
                                    }
                                }
                            )
                        )
                    ),
                ])

                const aggregatedRemoved: AggregatedRemovedClient[] = []
                const aggregatedActive: AggregatedActiveClient[] = []
                const reasonsMap: Record<string, ReasonCount[]> = {}

                allBatches.forEach((batch, idx) => {
                    removedResults[idx].forEach((client) => {
                        aggregatedRemoved.push({
                            ...client,
                            batch_id: batch.batch_id,
                            introducer: batch.introducer || 'N/A',
                        })
                    })

                    const reasons = reasonsResponses[idx]?.data
                    reasonsMap[batch.batch_id] = reasons?.success && Array.isArray(reasons.reasons)
                        ? reasons.reasons
                        : []

                    activeResults[idx].forEach((client) => {
                        aggregatedActive.push({
                            ...client,
                            batch_id: batch.batch_id,
                        })
                    })
                })

                setRemovedClients(aggregatedRemoved)
                setActiveClients(aggregatedActive)
                setReasonsByBatch(reasonsMap)
                setBatchAllocateSummaries(summariesMap)
            } catch (err: any) {
                if (err?.response?.status === 401) {
                    Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                    navigate('/login')
                    return
                }
                setError(err?.message || 'Failed to load funding failures data')
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [navigate])

    const totals = useMemo(() => {
        const totalRemovedClaims = removedClients.reduce((sum, c) => sum + c.claimsCount, 0)
        const batchesAffected = new Set(removedClients.map((c) => c.batch_id)).size
        const totalValueRemoved = removedClients.reduce((sum, c) => sum + (c.totalValue || 0), 0)
        return {
            totalRemovedClients: removedClients.length,
            totalRemovedClaims,
            batchesAffected,
            totalValueRemoved,
        }
    }, [removedClients])

    const reasonBreakdown = useMemo(() => {
        const counts: Record<string, number> = {}
        Object.values(reasonsByBatch).forEach((reasons) => {
            reasons.forEach((r) => {
                counts[r.reason] = (counts[r.reason] || 0) + r.count
            })
        })
        return Object.entries(counts)
            .map(([reason, count]) => ({ reason, count }))
            .sort((a, b) => b.count - a.count)
    }, [reasonsByBatch])

    const introducers = useMemo(() => {
        const set = new Set<string>()
        removedClients.forEach((c) => set.add(c.introducer))
        return Array.from(set).sort()
    }, [removedClients])

    const filteredClients = useMemo(() => {
        const searchLower = search.trim().toLowerCase()
        return removedClients.filter((c) => {
            if (batchFilter !== 'all' && c.batch_id !== batchFilter) return false
            if (introducerFilter !== 'all' && c.introducer !== introducerFilter) return false
            if (searchLower) {
                const haystack = [
                    c.first_name,
                    c.last_name,
                    c.email_address,
                    c.batch_id,
                    c.introducer,
                    ...(c.claims || []).map((cl) => cl.funder_case_ref),
                    ...(c.claims || []).map((cl) => cl.law_firm_secondary_reference),
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                if (!haystack.includes(searchLower)) return false
            }
            return true
        })
    }, [removedClients, batchFilter, introducerFilter, search])

    const cohortStats = useMemo(() => {
        const scopedRemoved = batchFilter === 'all'
            ? removedClients
            : removedClients.filter((c) => c.batch_id === batchFilter)

        const scopedActive = batchFilter === 'all'
            ? activeClients
            : activeClients.filter((c) => c.batch_id === batchFilter)

        const removedClaims: NormClaim[] = scopedRemoved.flatMap((c) =>
            (c.claims || []).map((cl) => ({
                defendant: cl.defendant,
                client_status: cl.client_status,
                cfa_date: cl.cfa_date,
                loan_value: cl.loan_value,
                quantum_individual: cl.quantum_individual,
            }))
        )

        const activeClaims: NormClaim[] = scopedActive.flatMap((c) =>
            (c.claims || []).map((cl) => ({
                defendant: cl.defendant,
                client_status: cl.client_status,
                cfa_date: cl.cfa_date,
                loan_value: cl.loan_value,
                quantum_individual: cl.quantum_individual,
            }))
        )

        const removedTotal = scopedRemoved.reduce((s, c) => s + (c.totalValue || 0), 0)
        const allocSummary = batchFilter !== 'all' ? batchAllocateSummaries[batchFilter] : undefined
        const activeTotal = allocSummary?.total_book_value ?? scopedActive.reduce((s, c) => s + (c.total_value || 0), 0)

        return {
            stayed: deriveCohortStats(activeClaims, scopedActive.length, activeTotal),
            removed: deriveCohortStats(removedClaims, scopedRemoved.length, removedTotal),
        }
    }, [removedClients, activeClients, batchFilter, batchAllocateSummaries])

    const scopedRawClaims = useMemo(() => {
        const scopedRemoved = batchFilter === 'all' ? removedClients : removedClients.filter((c) => c.batch_id === batchFilter)
        const scopedActive = batchFilter === 'all' ? activeClients : activeClients.filter((c) => c.batch_id === batchFilter)

        return {
            stayed: scopedActive.flatMap((c) =>
                (c.claims || []).map((cl) => ({
                    funder_client_id: c.funder_client_id,
                    funder_case_ref: cl.funder_case_ref,
                    introducer_case_ref: cl.introducer_case_ref,
                    law_firm_secondary_reference: cl.law_firm_secondary_reference,
                    defendant: cl.defendant,
                    client_status: cl.client_status,
                    cfa_date: cl.cfa_date,
                    loan_value: cl.loan_value,
                    quantum_individual: cl.quantum_individual,
                }))
            ),
            removed: scopedRemoved.flatMap((c) =>
                (c.claims || []).map((cl) => ({
                    funder_client_id: c.funder_client_id,
                    funder_case_ref: cl.funder_case_ref,
                    introducer_case_ref: cl.introducer_case_ref,
                    law_firm_secondary_reference: cl.law_firm_secondary_reference,
                    agreement_number: cl.agreement_number,
                    agreement_start_date: cl.agreement_start_date,
                    batch_id: c.batch_id,
                    introducer: c.introducer,
                    defendant: cl.defendant,
                    client_status: cl.client_status,
                    cfa_date: cl.cfa_date,
                    loan_value: cl.loan_value,
                    quantum_individual: cl.quantum_individual,
                    audit_removed_date: cl.audit_removed_date,
                }))
            ),
        }
    }, [removedClients, activeClients, batchFilter])

    const chartData = useMemo(() => {
        const { stayed, removed } = scopedRawClaims
        return {
            defendant: buildDefendantChart(stayed, removed),
            status: buildStatusChart(stayed, removed),
            loan: buildLoanChart(stayed, removed),
            cfa: buildCfaChart(stayed, removed),
        }
    }, [scopedRawClaims])

    const missingFieldsRows = useMemo(
        () => buildMissingFieldsTable(scopedRawClaims.stayed, scopedRawClaims.removed),
        [scopedRawClaims]
    )

    const passRateRows = useMemo(
        () => buildPassRateTable(scopedRawClaims.stayed, scopedRawClaims.removed),
        [scopedRawClaims]
    )

    const explorerBounds = useMemo(() => {
        const claims = scopedRawClaims.removed
        const loanValues = claims.map((c) => c.loan_value || 0)
        const quantumValues = claims.map((c) => c.quantum_individual || 0)
        return {
            loanMax: loanValues.length ? Math.ceil(Math.max(...loanValues)) : 10000,
            quantumMax: quantumValues.length ? Math.ceil(Math.max(...quantumValues)) : 10000,
            defendants: Array.from(new Set(claims.map((c) => c.defendant || 'Unknown'))).sort(),
            introducers: Array.from(new Set(claims.map((c) => c.introducer || 'Unknown'))).sort(),
            batches: Array.from(new Set(claims.map((c) => c.batch_id || 'Unknown'))).sort(),
            statuses: Array.from(new Set(claims.map((c) => c.client_status || 'Unknown'))).sort(),
        }
    }, [scopedRawClaims])

    const explorerDefendantCounts = useMemo(() => (
        scopedRawClaims.removed.reduce<Record<string, number>>((acc, c) => {
            const defendant = c.defendant || 'Unknown'
            acc[defendant] = (acc[defendant] || 0) + 1
            return acc
        }, {})
    ), [scopedRawClaims])

    const explorerIntroducerCounts = useMemo(() => (
        scopedRawClaims.removed.reduce<Record<string, number>>((acc, c) => {
            const introducer = c.introducer || 'Unknown'
            acc[introducer] = (acc[introducer] || 0) + 1
            return acc
        }, {})
    ), [scopedRawClaims])

    const explorerBatchCounts = useMemo(() => (
        scopedRawClaims.removed.reduce<Record<string, number>>((acc, c) => {
            const batch = c.batch_id || 'Unknown'
            acc[batch] = (acc[batch] || 0) + 1
            return acc
        }, {})
    ), [scopedRawClaims])

    const explorerBatchClientCounts = useMemo(() => (
        scopedRawClaims.removed.reduce<Record<string, Set<number>>>((acc, c) => {
            const batch = c.batch_id || 'Unknown'
            if (!acc[batch]) acc[batch] = new Set()
            if (typeof c.funder_client_id === 'number') acc[batch].add(c.funder_client_id)
            return acc
        }, {})
    ), [scopedRawClaims])

    const explorerLoanValueCounts = useMemo(() => (
        scopedRawClaims.removed.reduce<Record<number, number>>((acc, c) => {
            const value = c.loan_value || 0
            acc[value] = (acc[value] || 0) + 1
            return acc
        }, {})
    ), [scopedRawClaims])

    const explorerLoanValues = useMemo(() => (
        Object.keys(explorerLoanValueCounts)
            .map(Number)
            .sort((a, b) => a - b)
    ), [explorerLoanValueCounts])

    const sortedExplorerDefendants = useMemo(() => {
        const defendants = [...explorerBounds.defendants]
        return defendants.sort((a, b) => {
            if (defendantSort === 'claims') {
                const countDiff = (explorerDefendantCounts[b] || 0) - (explorerDefendantCounts[a] || 0)
                if (countDiff !== 0) return countDiff
            }
            return a.localeCompare(b)
        })
    }, [explorerBounds.defendants, explorerDefendantCounts, defendantSort])

    const sortedExplorerIntroducers = useMemo(() => (
        [...explorerBounds.introducers].sort((a, b) => {
            const countDiff = (explorerIntroducerCounts[b] || 0) - (explorerIntroducerCounts[a] || 0)
            if (countDiff !== 0) return countDiff
            return a.localeCompare(b)
        })
    ), [explorerBounds.introducers, explorerIntroducerCounts])

    const sortedExplorerBatches = useMemo(() => (
        [...explorerBounds.batches].sort((a, b) => {
            const countDiff = (explorerBatchCounts[b] || 0) - (explorerBatchCounts[a] || 0)
            if (countDiff !== 0) return countDiff
            return a.localeCompare(b)
        })
    ), [explorerBounds.batches, explorerBatchCounts])

    useEffect(() => {
        setExplorerLoanValuesSelected(new Set())
        setExplorerQuantumMin(0)
        setExplorerQuantumMax(explorerBounds.quantumMax)
        setPendingQuantumMin(0)
        setPendingQuantumMax(explorerBounds.quantumMax)
        setExplorerDefendants([])
        setExplorerIntroducers([])
        setExplorerBatches([])
        setExplorerStatuses([])
        setExplorerCfa('all')
    }, [explorerBounds])

    useEffect(() => {
        if (openExplorerFilter !== 'quantum') return
        setPendingQuantumMin(explorerQuantumMin)
        setPendingQuantumMax(explorerQuantumMax)
    }, [openExplorerFilter, explorerQuantumMin, explorerQuantumMax])

    const explorerFiltered = useMemo(() => {
        const quantumMax = explorerQuantumMax === Infinity ? explorerBounds.quantumMax : explorerQuantumMax
        return scopedRawClaims.removed.filter((c) => {
            const loan = c.loan_value || 0
            const quantum = c.quantum_individual || 0
            if (explorerLoanValuesSelected.size > 0 && !explorerLoanValuesSelected.has(loan)) return false
            if (quantum < explorerQuantumMin || quantum > quantumMax) return false
            if (explorerDefendants.length > 0 && !explorerDefendants.includes(c.defendant || 'Unknown')) return false
            if (explorerIntroducers.length > 0 && !explorerIntroducers.includes(c.introducer || 'Unknown')) return false
            if (explorerBatches.length > 0 && !explorerBatches.includes(c.batch_id || 'Unknown')) return false
            if (explorerStatuses.length > 0 && !explorerStatuses.includes(c.client_status || 'Unknown')) return false
            if (explorerCfa === 'present' && !c.cfa_date) return false
            if (explorerCfa === 'missing' && c.cfa_date) return false
            return true
        })
    }, [scopedRawClaims, explorerLoanValuesSelected, explorerQuantumMin, explorerQuantumMax, explorerDefendants, explorerIntroducers, explorerBatches, explorerStatuses, explorerCfa, explorerBounds])

    const explorerFilteredNoQuantum = useMemo(() => {
        return scopedRawClaims.removed.filter((c) => {
            const loan = c.loan_value || 0
            if (explorerLoanValuesSelected.size > 0 && !explorerLoanValuesSelected.has(loan)) return false
            if (explorerDefendants.length > 0 && !explorerDefendants.includes(c.defendant || 'Unknown')) return false
            if (explorerIntroducers.length > 0 && !explorerIntroducers.includes(c.introducer || 'Unknown')) return false
            if (explorerBatches.length > 0 && !explorerBatches.includes(c.batch_id || 'Unknown')) return false
            if (explorerStatuses.length > 0 && !explorerStatuses.includes(c.client_status || 'Unknown')) return false
            if (explorerCfa === 'present' && !c.cfa_date) return false
            if (explorerCfa === 'missing' && c.cfa_date) return false
            return true
        })
    }, [scopedRawClaims, explorerLoanValuesSelected, explorerDefendants, explorerIntroducers, explorerBatches, explorerStatuses, explorerCfa])

    const tableClients = useMemo(() => {
        const explorerClientIds = new Set(
            explorerFiltered.map((c) => c.funder_client_id).filter((id): id is number => typeof id === 'number')
        )
        return filteredClients.filter((c) => explorerClientIds.has(c.funder_client_id))
    }, [filteredClients, explorerFiltered])

    // Reset to page 1 whenever the filtered set changes
    useEffect(() => { setCurrentPage(1) }, [tableClients])

    const totalPages = Math.max(1, Math.ceil(tableClients.length / INELIGIBLE_PAGE_SIZE))

    const paginatedClients = useMemo(() => {
        const start = (currentPage - 1) * INELIGIBLE_PAGE_SIZE
        return tableClients.slice(start, start + INELIGIBLE_PAGE_SIZE)
    }, [tableClients, currentPage])

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const toggleExplorerSet = (
        value: string,
        current: string[],
        set: (v: string[]) => void
    ) => {
        set(current.includes(value) ? current.filter((v) => v !== value) : [...current, value])
    }

    const resetExplorer = () => {
        setSearch('')
        setExplorerLoanValuesSelected(new Set())
        setExplorerQuantumMin(0)
        setExplorerQuantumMax(explorerBounds.quantumMax)
        setExplorerDefendants([])
        setExplorerIntroducers([])
        setExplorerBatches([])
        setExplorerStatuses([])
        setExplorerCfa('all')
        setOpenExplorerFilter(null)
        setFilterPopoverPosition(null)
    }

    useEffect(() => {
        if (!openExplorerFilter) return

        const handleOutsideFilterClick = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null
            if (target && explorerFilterBarRef.current?.contains(target)) return
            setOpenExplorerFilter(null)
            setFilterPopoverPosition(null)
        }

        document.addEventListener('mousedown', handleOutsideFilterClick)
        document.addEventListener('touchstart', handleOutsideFilterClick)

        return () => {
            document.removeEventListener('mousedown', handleOutsideFilterClick)
            document.removeEventListener('touchstart', handleOutsideFilterClick)
        }
    }, [openExplorerFilter])

    const toggleExplorerFilter = (
        filter: ExplorerFilterKey,
        event: ReactMouseEvent<HTMLButtonElement>,
        width = 430
    ) => {
        if (openExplorerFilter === filter) {
            setOpenExplorerFilter(null)
            setFilterPopoverPosition(null)
            return
        }

        const rect = event.currentTarget.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const actualWidth = Math.min(width, viewportWidth - 24)
        const left = Math.max(12, Math.min(rect.left + rect.width / 2 - actualWidth / 2, viewportWidth - actualWidth - 12))
        setFilterPopoverPosition({
            top: rect.bottom + 8,
            left,
            width: actualWidth,
        })
        setOpenExplorerFilter(filter)
    }

    const quantumMax = explorerQuantumMax === Infinity ? explorerBounds.quantumMax : explorerQuantumMax
    const hasSearch = search.trim().length > 0
    const searchSummary = search.trim().length > 34 ? `${search.trim().slice(0, 34)}...` : search.trim()
    const hasLoanFilter = explorerLoanValuesSelected.size > 0
    const hasQuantumFilter = explorerQuantumMin > 0 || explorerQuantumMax < explorerBounds.quantumMax
    const hasCfaFilter = explorerCfa !== 'all'
    const hasDefendantFilter = explorerDefendants.length > 0
    const hasIntroducerFilter = explorerIntroducers.length > 0
    const hasBatchFilter = explorerBatches.length > 0
    const hasStatusFilter = explorerStatuses.length > 0
    const loanFilterSummary = explorerLoanValuesSelected.size === 1
        ? formatCurrency(Array.from(explorerLoanValuesSelected)[0])
        : `${explorerLoanValuesSelected.size} selected`
    const quantumFilterSummary = `${formatCurrency(explorerQuantumMin)} - ${formatCurrency(quantumMax)}`
    const cfaFilterSummary = explorerCfa === 'present' ? 'Has date' : explorerCfa === 'missing' ? 'Missing date' : ''
    const defendantFilterSummary = explorerDefendants.length === 1 ? explorerDefendants[0] : `${explorerDefendants.length} selected`
    const introducerFilterSummary = explorerIntroducers.length === 1 ? explorerIntroducers[0] : `${explorerIntroducers.length} selected`
    const batchFilterSummary = explorerBatches.length === 1 ? explorerBatches[0] : `${explorerBatches.length} selected`
    const statusFilterSummary = explorerStatuses.length === 1 ? explorerStatuses[0] : `${explorerStatuses.length} selected`

    const resetExplorerFilter = (filter: ExplorerFilterKey) => {
        if (filter === 'search') setSearch('')
        if (filter === 'loan') {
            setExplorerLoanValuesSelected(new Set())
        }
        if (filter === 'quantum') {
            setExplorerQuantumMin(0)
            setExplorerQuantumMax(explorerBounds.quantumMax)
        }
        if (filter === 'cfa') setExplorerCfa('all')
        if (filter === 'defendant') setExplorerDefendants([])
        if (filter === 'introducer') setExplorerIntroducers([])
        if (filter === 'batch') setExplorerBatches([])
        if (filter === 'status') setExplorerStatuses([])
        if (openExplorerFilter === filter) {
            setOpenExplorerFilter(null)
            setFilterPopoverPosition(null)
        }
    }

    const explorerSelectionRemoved = explorerFiltered
    const explorerMetricPct = scopedRawClaims.removed.length > 0
        ? (explorerSelectionRemoved.length / scopedRawClaims.removed.length) * 100
        : 0
    const explorerSelectedClaimValue = explorerSelectionRemoved.reduce((sum, claim) => sum + (claim.quantum_individual || 0), 0)
    const explorerTotalClaimValue = scopedRawClaims.removed.reduce((sum, claim) => sum + (claim.quantum_individual || 0), 0)
    const explorerClaimValuePct = explorerTotalClaimValue > 0
        ? (explorerSelectedClaimValue / explorerTotalClaimValue) * 100
        : 0
    const explorerSelectedClients = new Set(
        explorerSelectionRemoved.map((claim) => claim.funder_client_id).filter((id): id is number => typeof id === 'number')
    ).size
    const explorerTotalClients = new Set(
        scopedRawClaims.removed.map((claim) => claim.funder_client_id).filter((id): id is number => typeof id === 'number')
    ).size
    const explorerClientPct = explorerTotalClients > 0
        ? (explorerSelectedClients / explorerTotalClients) * 100
        : 0
    const allClaimsTotal = scopedRawClaims.stayed.length + scopedRawClaims.removed.length
    const allClaimsFailureRate = allClaimsTotal > 0
        ? (scopedRawClaims.removed.length / allClaimsTotal) * 100
        : 0

    const toggleClient = (key: string) => {
        setExpandedClients((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const removedClientById = useMemo(() => {
        const m = new Map<number, AggregatedRemovedClient>()
        removedClients.forEach((c) => m.set(c.funder_client_id, c))
        return m
    }, [removedClients])

    const escapeCsvCell = (value: string): string => {
        if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes('\r')) {
            return `"${value.replace(/"/g, '""')}"`
        }
        return value
    }

    const formatDateForExport = (raw?: string | null): string => {
        if (raw == null || raw === '') return ''
        try {
            const d = new Date(raw)
            if (isNaN(d.getTime())) return String(raw)
            return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })
        } catch {
            return String(raw)
        }
    }

    const exportExplorerResultsCsv = useCallback(() => {
        const headers = [
            'Lender',
            'Date of loan',
            'Client ID',
            'Client email',
            'Client title',
            'Client first name',
            'Client last name',
            'Client date of birth',
            'Agreement number',
            'Batch',
            'Batch introducer',
            'Funder case ref',
            'Introducer case ref',
            'Law firm secondary reference',
            'Quantum',
            'CFA date',
        ]
        const lines: string[] = [headers.join(',')]
        for (const claim of explorerSelectionRemoved) {
            const client =
                typeof claim.funder_client_id === 'number' ? removedClientById.get(claim.funder_client_id) : undefined
            const loanDateRaw = claim.agreement_start_date || claim.cfa_date || ''
            const row = [
                claim.defendant || '',
                formatDateForExport(loanDateRaw),
                typeof claim.funder_client_id === 'number' ? String(claim.funder_client_id) : '',
                client?.email_address != null ? String(client.email_address) : '',
                client?.title != null ? String(client.title) : '',
                client?.first_name != null ? String(client.first_name) : '',
                client?.last_name != null ? String(client.last_name) : '',
                client?.dob ? formatDateForExport(client.dob) : '',
                claim.agreement_number != null && String(claim.agreement_number).trim() !== ''
                    ? String(claim.agreement_number)
                    : '',
                claim.batch_id != null ? String(claim.batch_id) : '',
                claim.introducer != null ? String(claim.introducer) : '',
                claim.funder_case_ref != null ? String(claim.funder_case_ref) : '',
                claim.introducer_case_ref != null ? String(claim.introducer_case_ref) : '',
                claim.law_firm_secondary_reference != null ? String(claim.law_firm_secondary_reference) : '',
                formatCurrency(claim.quantum_individual ?? 0),
                formatDateForExport(claim.cfa_date),
            ].map((cell) => escapeCsvCell(cell))
            lines.push(row.join(','))
        }
        const csv = `\uFEFF${lines.join('\n')}`
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `ineligible-funding-clients-${new Date().toISOString().slice(0, 10)}.csv`
        a.rel = 'noopener'
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
    }, [explorerSelectionRemoved, removedClientById])

    return {
        loading,
        error,
        batches,
        removedClients,
        batchFilter,
        setBatchFilter,
        introducerFilter,
        setIntroducerFilter,
        search,
        setSearch,
        expandedClients,
        detailsModalClientId,
        setDetailsModalClientId,
        totals,
        reasonBreakdown,
        introducers,
        filteredClients,
        cohortStats,
        scopedRawClaims,
        chartData,
        missingFieldsRows,
        passRateRows,
        explorerBounds,
        explorerDefendantCounts,
        explorerIntroducerCounts,
        explorerBatchCounts,
        explorerBatchClientCounts,
        explorerLoanValueCounts,
        explorerLoanValues,
        explorerLoanValuesSelected,
        setExplorerLoanValuesSelected,
        explorerQuantumMin,
        setExplorerQuantumMin,
        explorerQuantumMax,
        setExplorerQuantumMax,
        pendingQuantumMin,
        setPendingQuantumMin,
        pendingQuantumMax,
        setPendingQuantumMax,
        quantumApplying,
        setQuantumApplying,
        explorerDefendants,
        setExplorerDefendants,
        explorerIntroducers,
        setExplorerIntroducers,
        explorerBatches,
        setExplorerBatches,
        explorerStatuses,
        setExplorerStatuses,
        explorerCfa,
        setExplorerCfa,
        defendantSort,
        setDefendantSort,
        showExplorerClaimsModal,
        setShowExplorerClaimsModal,
        openExplorerFilter,
        setOpenExplorerFilter,
        filterPopoverPosition,
        setFilterPopoverPosition,
        explorerFilterBarRef,
        sortedExplorerDefendants,
        sortedExplorerIntroducers,
        sortedExplorerBatches,
        explorerFiltered,
        explorerFilteredNoQuantum,
        tableClients,
        paginatedClients,
        currentPage,
        totalPages,
        handlePageChange,
        toggleExplorerSet,
        resetExplorer,
        toggleExplorerFilter,
        quantumMax,
        hasSearch,
        searchSummary,
        hasLoanFilter,
        hasQuantumFilter,
        hasCfaFilter,
        hasDefendantFilter,
        hasIntroducerFilter,
        hasBatchFilter,
        hasStatusFilter,
        loanFilterSummary,
        quantumFilterSummary,
        cfaFilterSummary,
        defendantFilterSummary,
        introducerFilterSummary,
        batchFilterSummary,
        statusFilterSummary,
        resetExplorerFilter,
        explorerSelectionRemoved,
        explorerMetricPct,
        explorerSelectedClaimValue,
        explorerTotalClaimValue,
        explorerClaimValuePct,
        explorerSelectedClients,
        explorerTotalClients,
        explorerClientPct,
        allClaimsTotal,
        allClaimsFailureRate,
        toggleClient,
        exportExplorerResultsCsv,
    }
}
